import { openDB, IDBPDatabase } from 'idb';

interface OfflineMessage {
  id: string;
  sender_id: string;
  receiver_id: string;
  message: string;
  created_at: string;
  read_at?: string;
  synced: boolean;
  retry_count: number;
}

interface MessageQueue {
  id: string;
  action: 'send' | 'read' | 'edit' | 'delete';
  data: any;
  timestamp: string;
  retry_count: number;
}

class OfflineMessagingService {
  private db: IDBPDatabase | null = null;
  private readonly DB_NAME = 'agrosense_messages';
  private readonly DB_VERSION = 1;

  async init() {
    if (this.db) return;
    
    this.db = await openDB(this.DB_NAME, this.DB_VERSION, {
      upgrade(db) {
        // Messages store
        if (!db.objectStoreNames.contains('messages')) {
          const messageStore = db.createObjectStore('messages', { keyPath: 'id' });
          messageStore.createIndex('sender_id', 'sender_id');
          messageStore.createIndex('receiver_id', 'receiver_id');
          messageStore.createIndex('created_at', 'created_at');
          messageStore.createIndex('synced', 'synced');
        }

        // Queue store for pending operations
        if (!db.objectStoreNames.contains('queue')) {
          const queueStore = db.createObjectStore('queue', { keyPath: 'id' });
          queueStore.createIndex('timestamp', 'timestamp');
          queueStore.createIndex('action', 'action');
        }

        // Conversations store
        if (!db.objectStoreNames.contains('conversations')) {
          const convStore = db.createObjectStore('conversations', { keyPath: 'id' });
          convStore.createIndex('participants', ['participant_1', 'participant_2']);
          convStore.createIndex('last_message_at', 'last_message_at');
        }

        // User profiles store
        if (!db.objectStoreNames.contains('profiles')) {
          const profileStore = db.createObjectStore('profiles', { keyPath: 'id' });
          profileStore.createIndex('email', 'email');
        }
      }
    });
  }

  async storeMessage(message: OfflineMessage) {
    await this.init();
    if (!this.db) return;
    
    await this.db.put('messages', message);
  }

  async getMessages(userId: string): Promise<OfflineMessage[]> {
    await this.init();
    if (!this.db) return [];

    const tx = this.db.transaction('messages', 'readonly');
    const store = tx.objectStore('messages');
    const messages = await store.getAll();
    
    return messages.filter(msg => 
      msg.sender_id === userId || msg.receiver_id === userId
    ).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }

  async getUnreadCount(userId: string): Promise<number> {
    await this.init();
    if (!this.db) return 0;

    try {
      const messages = await this.getMessages(userId);
      const unreadMessages = messages.filter(msg => 
        msg.receiver_id === userId && !msg.read_at
      );
      return unreadMessages.length;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  async queueOperation(operation: MessageQueue) {
    await this.init();
    if (!this.db) return;
    
    await this.db.put('queue', operation);
  }

  async getQueuedOperations(): Promise<MessageQueue[]> {
    await this.init();
    if (!this.db) return [];
    
    return await this.db.getAll('queue');
  }

  async removeFromQueue(id: string) {
    await this.init();
    if (!this.db) return;
    
    await this.db.delete('queue', id);
  }

  async syncWithServer(supabaseClient: any) {
    const operations = await this.getQueuedOperations();
    
    for (const op of operations) {
      try {
        switch (op.action) {
          case 'send':
            await supabaseClient.from('messages').insert(op.data);
            break;
          case 'read':
            await supabaseClient.from('messages')
              .update({ read_at: op.data.read_at })
              .eq('id', op.data.id);
            break;
          case 'edit':
            await supabaseClient.from('messages')
              .update({ message: op.data.message, edited_at: new Date().toISOString() })
              .eq('id', op.data.id);
            break;
          case 'delete':
            await supabaseClient.from('messages')
              .update({ is_deleted: true })
              .eq('id', op.data.id);
            break;
        }
        
        await this.removeFromQueue(op.id);
      } catch (error) {
        console.warn('Sync failed for operation:', op.id, error);
        
        // Increment retry count
        op.retry_count = (op.retry_count || 0) + 1;
        if (op.retry_count < 3) {
          await this.queueOperation(op);
        } else {
          await this.removeFromQueue(op.id);
        }
      }
    }
  }

  async deleteMessage(messageId: string) {
    await this.init();
    if (!this.db) return;
    
    await this.db.delete('messages', messageId);
  }

  async clearAll() {
    await this.init();
    if (!this.db) return;
    
    const tx = this.db.transaction(['messages', 'queue', 'conversations', 'profiles'], 'readwrite');
    await Promise.all([
      tx.objectStore('messages').clear(),
      tx.objectStore('queue').clear(),
      tx.objectStore('conversations').clear(),
      tx.objectStore('profiles').clear()
    ]);
  }
}

export const offlineMessaging = new OfflineMessagingService();